// Static starter templates — ready-made graphs inserted via store.loadGraph().
// Node ids use the n<number> convention so the store's id counter resets correctly on load.

import { type Node, type Edge } from '@xyflow/react';
import { NODE_SPECS } from './registry';

const n = (id: string, type: string, x: number, y: number, data: Record<string, unknown> = {}): Node => ({
  id,
  type,
  position: { x, y },
  data: { ...(NODE_SPECS[type]?.defaultData ?? {}), ...data },
});

const e = (id: string, s: string, sh: string, t: string, th: string): Edge => ({
  id,
  source: s,
  sourceHandle: sh,
  target: t,
  targetHandle: th,
});

export interface PipelineTemplate {
  id: string;
  name: string;
  description: string;
  build: () => { nodes: Node[]; edges: Edge[] };
}

export const TEMPLATES: PipelineTemplate[] = [
  {
    id: 't2i',
    name: 'Text → Image → Upscale → Save',
    description: 'Generate from a prompt, upscale 2×, save to the gallery.',
    build: () => ({
      nodes: [
        n('n1', 'promptInput', 0, 40, { text: 'a serene mountain lake at sunrise, cinematic' }),
        n('n2', 'generate', 300, 40),
        n('n3', 'upscale', 600, 40, { scale: '2' }),
        n('n4', 'saveToGallery', 900, 40),
      ],
      edges: [
        e('e1', 'n1', 'text', 'n2', 'prompt'),
        e('e2', 'n2', 'image', 'n3', 'image'),
        e('e3', 'n3', 'image', 'n4', 'image'),
      ],
    }),
  },
  {
    id: 'rmbg',
    name: 'Photo → Remove BG → Save',
    description: 'Pick a gallery image, remove its background, save the cutout.',
    build: () => ({
      nodes: [
        n('n1', 'imageGallery', 0, 40),
        n('n2', 'removeBackground', 300, 40),
        n('n3', 'saveToGallery', 600, 40),
      ],
      edges: [e('e1', 'n1', 'image', 'n2', 'image'), e('e2', 'n2', 'image', 'n3', 'image')],
    }),
  },
  {
    id: 'detseg',
    name: 'Detect → Segment',
    description: 'Detect objects (YOLO) and segment the top box with SAM2.',
    build: () => ({
      nodes: [
        n('n1', 'imageGallery', 0, 60),
        n('n2', 'detect', 300, -40),
        n('n3', 'segment', 600, 60),
        n('n4', 'preview', 900, 60),
      ],
      edges: [
        e('e1', 'n1', 'image', 'n2', 'image'),
        e('e2', 'n1', 'image', 'n3', 'image'),
        e('e3', 'n2', 'detections', 'n3', 'boxes'),
        e('e4', 'n3', 'mask', 'n4', 'image'),
      ],
    }),
  },
  {
    id: 'cap2gen',
    name: 'Caption → Prompt → Generate',
    description: 'Describe an image, splice it into a prompt, generate a new one.',
    build: () => ({
      nodes: [
        n('n1', 'imageGallery', 0, 40),
        n('n2', 'caption', 280, 40),
        n('n3', 'promptInput', 540, 40, { text: 'a watercolor painting of {input}' }),
        n('n4', 'generate', 820, 40),
        n('n5', 'preview', 1100, 40),
      ],
      edges: [
        e('e1', 'n1', 'image', 'n2', 'image'),
        e('e2', 'n2', 'text', 'n3', 'input'),
        e('e3', 'n3', 'text', 'n4', 'prompt'),
        e('e4', 'n4', 'image', 'n5', 'image'),
      ],
    }),
  },
  {
    id: 'i2v',
    name: 'Image → Animate (I2V)',
    description: 'Animate a gallery image with a motion prompt (run on server).',
    build: () => ({
      nodes: [
        n('n1', 'imageGallery', 0, 60),
        n('n2', 'promptInput', 300, -40, { text: 'gentle camera push-in, subtle motion' }),
        n('n3', 'animateVideo', 600, 60),
      ],
      edges: [e('e1', 'n1', 'image', 'n3', 'image'), e('e2', 'n2', 'text', 'n3', 'prompt')],
    }),
  },
];
